'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { FaArrowRight, FaCheck, FaStar, FaTag } from 'react-icons/fa'

// The gems page's two discounts, each in its own place (Ruben, 12 October 2026):
// - PlanOfferBadge, the card's top-right corner: the plan's half price. A subscriber's is applied (a tick);
//   for Free it is an offer ("Get 50% off"), a link to /upgrade. Each with its plan's emoji, as the upgrade page has it. On every card in every set, so it never biases the price test.
// - GemPackSavings, under the price, only in the gem price test (promptchan-backend docs/experiments/gem-price-test.md):
//   the bulk value, "2.9× Better Value" (gems per dollar against the smallest pack, on list prices), so the card's one
//   percentage is the plan's 50%; the largest pack's in gold with a star. For subscribers only: Free sees nothing that
//   competes with the Pro offer.
// Neither during a Black Friday sale, whose own 70% badge says it all.

// The card's original badge (before 12 October 2026): top-right, a dark pill with a thin white border, "50% Off" over a
// small "🔥 Pro Deal" (the plan's emoji and name, gold for Pro). Kept, with a tick for a subscriber's (applied) and, for
// Free, "Get 50% Off →" as a link to /upgrade.
const CORNER =
  'absolute top-7 right-0 transform translate-x-2/2 -translate-y-1/2 mr-2 z-20'
const PILL =
  'block rounded-full border border-[#FEFEFE] bg-[#222323] px-7 py-1.5 text-center text-sm font-semibold leading-tight text-white'
// The tick or arrow to the right of both lines, centred on them vertically and outside their width, so "50% Off"
// and "Pro Deal" share one centre.
const MARK =
  'absolute left-full top-1/2 ml-2 flex -translate-y-1/2 items-center'
// Each plan's emoji, as the upgrade page shows it beside the plan's name (src/app/upgrade/page.jsx PLANS).
const PLAN_EMOJI = { Plus: '🎉', Premium: '🚀', Pro: '🔥', Max: '⚡️' }

function Deal({ name }) {
  const { t } = useTranslation()
  return (
    <div
      className={`flex items-center justify-center gap-1 text-[9px] font-bold tracking-wide ${
        name === 'Pro' ? 'text-[#FFB419]' : 'text-white'
      }`}
    >
      {PLAN_EMOJI[name]} {t('planDeal', '{{plan}} Deal', { plan: name })}
    </div>
  )
}

export function PlanOfferBadge({ halfPrice, plan }) {
  const { t } = useTranslation()
  if (halfPrice) {
    const name = PLAN_EMOJI[plan] ? plan : 'Pro'
    return (
      <div className={CORNER}>
        <div className={PILL}>
          <span className="relative inline-block">
            {t('fiftyOff', '50% Off')}
            <Deal name={name} />
            <span className={MARK}>
              <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#FFB419] text-black">
                <FaCheck size={6} />
              </span>
            </span>
          </span>
        </div>
      </div>
    )
  }
  return (
    <Link href="/upgrade" className={`${CORNER} group`}>
      <span className={`${PILL} transition group-hover:border-[#FFB419]`}>
        <span className="relative inline-block">
          {t('getFiftyOff', 'Get 50% Off')}
          <Deal name="Pro" />
          <span className={MARK}>
            <FaArrowRight
              size={8}
              className="text-[#FFB419] transition-transform group-hover:translate-x-0.5"
            />
          </span>
        </span>
      </span>
    </Link>
  )
}

export default function GemPackSavings({ gem, inTest, halfPrice }) {
  const { t } = useTranslation()
  if (!inTest || !halfPrice || !(gem.valueMultiple > 1)) return null
  return (
    <div className="-mt-5 mb-6 flex justify-center">
      <span
        title={t('vsSmallestPack', 'Compared with the {{gems}}-gem pack', {
          gems: gem.baseGems,
        })}
        className={
          gem.bestValue
            ? 'inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FFD36B] to-[#FF9F1C] px-3 py-1 text-[11px] font-extrabold text-black shadow-[0_0_16px_rgba(255,180,25,0.35)]'
            : 'inline-flex items-center gap-1.5 rounded-full border border-[#8B85FF]/35 bg-[#5B54E8]/30 px-3 py-1 text-[11px] font-bold text-[#E4E2FF]'
        }
      >
        {gem.bestValue ? <FaStar size={9} /> : <FaTag size={9} />}
        {t('betterValue', '{{multiple}}× Better Value', {
          multiple: gem.valueMultiple,
        })}
      </span>
    </div>
  )
}
